import { AdditionalData, PushEventType, TrackingEvent } from './pushToDataLayer';
import convertProductsToAnalyticsItems, {
  AnalyticsItem,
  AnalyticsProduct
} from './productsToAnalyticsItemConverter';
import getPageType, { PageType, PageTypeOptions } from '@spa/lib/tracking/getPageType';
import pushMultipleToDataLayer from '@spa/lib/tracking/pushMultipleToDataLayer';

export enum ProductActionsType {
  SELECT_ITEM = 'select_item',
  VIEW_ITEM = 'view_item',
  VIEW_ITEM_LIST = 'view_item_list',
  VIEW_SEARCH_ITEMS = 'view_search_items'
}

export enum ProductsActionOrigin {
  PDP = 'product_detail',
  PLP = 'product_list',
  CART = 'cart',
  PRODUCT_CAROUSEL = 'product_carousel',
  SEARCH_SUGGESTION = 'search_suggestion',
  SEARCH_RESULTS = 'search_results',
  NAVIGATION_FLYOUT = 'navigation_flyout'
}

export type ProductActionsOriginType = {
  readonly originId: string;
  readonly originType: ProductsActionOrigin | string;
};

export type TrackProductActionsType = {
  readonly type: ProductActionsType;
  readonly actionOrigin?: ProductActionsOriginType;
};

const getAdditionalData = (
  { actionOrigin, type }: TrackProductActionsType,
  products: ReadonlyArray<AnalyticsProduct>,
  searchMetadata: Record<string, string | undefined>,
  pageTypeOptions: PageTypeOptions,
  suppressPriceData: boolean
): AdditionalData => {
  const additionalData: AdditionalData = {
    searchMetadata: { ...searchMetadata }
  };

  const pageType: PageType = getPageType(pageTypeOptions);
  if (pageType !== undefined) {
    additionalData.page_type = pageType;
  }

  if (
    actionOrigin !== undefined &&
    (type === ProductActionsType.SELECT_ITEM || type === ProductActionsType.VIEW_SEARCH_ITEMS)
  ) {
    const { originId, originType }: ProductActionsOriginType = actionOrigin;
    additionalData.item_list_id = originId;
    additionalData.item_list_name = originType;
  } else if (!suppressPriceData) {
    const totalValue: number =
      products.reduce(
        (sum, product) => sum + Math.round(product.currentPrice.valueWithoutTax * 100),
        0
      ) / 100;

    additionalData.currency = products[0].currentPrice.currencyIso;
    additionalData.value = totalValue;
  }

  return additionalData;
};

const createEcommerceEvent = (
  eventType: TrackProductActionsType,
  anonymousId: string | null,
  authenticatedId: string | null,
  additionalData: AdditionalData,
  convertedProducts: Array<AnalyticsItem>
): PushEventType => ({
  type: TrackingEvent.ECOMMERCE,
  anonymousId,
  authenticatedId,
  event: eventType.type,
  ecommerce: {
    ...additionalData,
    items: convertedProducts
  }
});

export const trackProductActions = (
  eventType: TrackProductActionsType,
  products: ReadonlyArray<AnalyticsProduct>,
  anonymousId: string | null,
  authenticatedId: string | null,
  pageTypeOptions: PageTypeOptions,
  searchMetadata: Record<string, string | undefined>,
  isAfterSearch: boolean = true,
  suppressPriceData: boolean = false
): void => {
  const updatedSearchMetadata: Record<string, string | undefined> = isAfterSearch
    ? searchMetadata
    : { ...searchMetadata, queryId: undefined };

  const additionalData: AdditionalData = getAdditionalData(
    eventType,
    products,
    updatedSearchMetadata,
    pageTypeOptions,
    suppressPriceData
  );
  const convertedProducts: Array<AnalyticsItem> = convertProductsToAnalyticsItems(
    products,
    eventType.actionOrigin,
    undefined,
    undefined,
    suppressPriceData
  );

  const ecommerceEvent: PushEventType = createEcommerceEvent(
    eventType,
    anonymousId,
    authenticatedId,
    additionalData,
    convertedProducts
  );

  const events: Array<PushEventType> = [ecommerceEvent];

  if (
    eventType.type === ProductActionsType.VIEW_SEARCH_ITEMS &&
    additionalData.item_list_id &&
    'event' in ecommerceEvent
  ) {
    const viewItemListEvent: PushEventType = {
      ...ecommerceEvent,
      event: ProductActionsType.VIEW_ITEM_LIST
    };
    events.push(viewItemListEvent);
  }

  pushMultipleToDataLayer(events);
};

export default trackProductActions;
